import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import type { TortusClient, Transcription } from "@tortus-ai/embed-client";

import type {
  DictationChosenSource,
  DictationCompleteEvent,
  DictationNoteSection,
  Patient,
} from "@thalamos/aspire-common";
import { UserSessionEventAction } from "@thalamos/aspire-common";

import { useRecordUserSessionEvent } from "../../../hooks/useRecordUserSessionEvent.js";
import { PopupDialog } from "../PopupDialog/PopupDialog.js";
import { TortusAvtDrawer } from "./TortusAvtDrawer.js";
import { TortusAvtModal } from "./TortusAvtModal.js";
import { TortusDockedPanel } from "./TortusDockedPanel.js";
import type { TortusAvtConsultationCompletedData } from "./TortusEmbedSurface.js";
import {
  TortusResultChooser,
  type DictationInsertMode,
} from "./TortusResultChooser.js";
import { useTortusLaunchToken } from "./useTortusLaunchToken.js";

export type TortusDictationSessionProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Called with a rich event object once the consultation completes and
   * the user has picked one of the chooser cards. The event includes the
   * chosen text plus the full structured payload from Tortus so callers
   * can ignore the chooser entirely if they want richer post-processing.
   * If the consultation completes with no usable output, this is not
   * called.
   */
  onDictationComplete: (
    event: DictationCompleteEvent,
    insertMode: DictationInsertMode,
  ) => void;
  /**
   * The patient the form draft is being filled for. When provided, their
   * name, date of birth, and (if ExtendedPatient) NHS number are shown
   * inside the Tortus consultation UI. When omitted, Tortus shows blank
   * patient fields.
   */
  patient?: Patient;
  formDraftId?: string;
  /**
   * Form-level mode: skip the per-output chooser. As soon as the note comes
   * back, emit the full event (with all sections) so the caller can map it
   * across the form instead of inserting one chosen output.
   */
  skipChooser?: boolean;
  /** Forwarded to the chooser — see {@link TortusResultChooserProps.existingText}. */
  existingText?: string;
  /** Forwarded to the docked panel — see {@link TortusAvtDrawerProps.stickyTop}. */
  stickyTop?: number;
  /** Forwarded to the docked panel — see {@link TortusAvtDrawerProps.bottomInset}. */
  bottomInset?: number;
};

type DictationResults = {
  transcript: string | null;
  /** Best-effort plain-text rendering of the note for the "Full AI note"
   *  card. Markdown if available, stripped HTML as fallback. */
  noteForDisplay: string | null;
  /** Raw markdown straight from Tortus (used for section parsing + the
   *  `event.raw.noteMarkdown` pass-through). */
  noteMarkdown: string | null;
  /** Raw HTML straight from Tortus. */
  noteHtml: string | null;
  sections: DictationNoteSection[];
  medicalCodes: unknown[];
};

/**
 * Auto-starts a FACE_TO_FACE consultation in the docked Tortus panel as soon as
 * the SDK is ready, then offers the user a choice between the raw transcript and
 * the AI-generated medical note from the completion event.
 *
 * Why FACE_TO_FACE and not dictations.start()?
 * The SDK's `dictation:closed` event carries no payload, so there's no
 * documented way to get the dictated text back from `client.dictations`.
 * `consultations.start({ mode: "FACE_TO_FACE", ... })` produces a
 * `consultation:completed` event with `result.artifacts`, which is what
 * we need. Revisit if Tortus expose a dictation-result API.
 *
 * The chooser takes over the same panel the embed occupied, so the flow stays in
 * one place beside the form from speaking through to inserting the text.
 */
export const TortusDictationSession = ({
  open,
  onClose,
  onDictationComplete,
  patient,
  formDraftId,
  skipChooser = false,
  existingText,
  stickyTop,
  bottomInset,
}: TortusDictationSessionProps) => {
  const patientId = patient?.id;
  const tokenProvider = useTortusLaunchToken({ formDraftId, patientId });
  const recordEvent = useRecordUserSessionEvent();
  const theme = useTheme();
  // Only dock beside the form where there is genuinely room for both: the panel
  // is a fixed 520px and the app's left nav already takes ~250px, so anything
  // below `lg` leaves a form column too narrow to fill in. Narrower than that and
  // the embed takes over the screen via the dialog shell, which PopupDialog
  // renders full-screen on phones.
  //
  // Crossing this breakpoint swaps shells, which remounts the embed and so ends
  // a consultation in progress — a resize mid-dictation, not something a user
  // hits by scrolling or typing.
  const dockPanel = useMediaQuery(theme.breakpoints.up("lg"));
  const tortusPatient = mapPatientForTortus(patient);
  const [client, setClient] = useState<TortusClient | null>(null);
  const [pendingResults, setPendingResults] = useState<DictationResults | null>(
    null,
  );
  const startedRef = useRef(false);
  const onDictationCompleteRef = useRef(onDictationComplete);
  useEffect(() => {
    onDictationCompleteRef.current = onDictationComplete;
  });

  // Form-level mode: once results arrive, emit the whole note (all sections)
  // immediately instead of showing the per-output chooser.
  useEffect(() => {
    if (!skipChooser || !pendingResults) return;
    onDictationCompleteRef.current(
      {
        chosen:
          pendingResults.noteMarkdown ?? pendingResults.noteForDisplay ?? "",
        chosenSource: "note",
        raw: {
          transcript: pendingResults.transcript,
          noteMarkdown: pendingResults.noteMarkdown,
          noteHtml: pendingResults.noteHtml,
        },
        sections: pendingResults.sections,
        medicalCodes: pendingResults.medicalCodes,
      },
      "append",
    );
    setPendingResults(null);
  }, [skipChooser, pendingResults]);

  // Reset the "have we started a consultation yet" flag whenever the
  // modal closes, so re-opening starts a fresh consultation.
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setPendingResults(null);
    }
  }, [open]);

  useEffect(() => {
    if (!client || startedRef.current) return;
    startedRef.current = true;
    void (async () => {
      try {
        const consultationInput = {
          mode: "FACE_TO_FACE" as const,
          // Only pass `patient` when we have real data. Without this
          // Tortus would render placeholder text in their patient
          // panel.
          ...(tortusPatient ? { patient: tortusPatient } : {}),
          metadata: {
            ...(formDraftId ? { formDraftId } : {}),
            ...(patientId ? { patientId } : {}),
            source: "aspire.formDraft.inlineDictation",
          },
        };
        const consultationOptions = { returnTo: "standby" as const };
        await client.consultations.start(
          consultationInput,
          consultationOptions,
        );
      } catch {
        // Errors are surfaced via the modal's own error UI / onError prop.
      }
    })();
  }, [client, tortusPatient, formDraftId, patientId]);

  const handleValues = useCallback(
    (data: TortusAvtConsultationCompletedData) => {
      const artifacts = data.result.artifacts;

      const transcript = extractTranscript(artifacts.transcriptions ?? []);

      type MedicalNoteLike = {
        content: string;
        contentType: "text/markdown" | "text/html";
      };
      const notes: MedicalNoteLike[] = artifacts.medicalNotes ?? [];
      const mdNote = notes.find(
        (n: MedicalNoteLike) => n.contentType === "text/markdown",
      );
      const htmlNote = notes.find(
        (n: MedicalNoteLike) => n.contentType === "text/html",
      );
      const noteMarkdown =
        mdNote && mdNote.content.trim().length > 0 ? mdNote.content : null;
      const noteHtml =
        htmlNote && htmlNote.content.trim().length > 0
          ? htmlNote.content
          : null;
      const noteForDisplay =
        noteMarkdown ?? (noteHtml ? stripHtml(noteHtml) : null);

      const sections = splitNoteIntoSections(noteMarkdown);

      if (!transcript && !noteForDisplay && sections.length === 0) return;

      setPendingResults({
        transcript,
        noteForDisplay,
        noteMarkdown,
        noteHtml,
        sections,
        medicalCodes: artifacts.medicalCodes ?? [],
      });
    },
    [],
  );

  // Only user-initiated closes reach here; a completed dictation unmounts this instead.
  const handleClose = () => {
    recordEvent(
      pendingResults
        ? UserSessionEventAction.avtDiscardNote
        : UserSessionEventAction.avtAbandonDictation,
      formDraftId,
    );
    onClose();
  };

  const pickResult = (args: {
    chosen: string;
    chosenSource: DictationChosenSource;
    insertMode: DictationInsertMode;
  }) => {
    if (!pendingResults) return;
    recordEvent(
      args.chosenSource === "transcript"
        ? UserSessionEventAction.avtUseTranscript
        : UserSessionEventAction.avtUseAiNote,
      formDraftId,
    );
    const event: DictationCompleteEvent = {
      chosen: args.chosen,
      chosenSource: args.chosenSource,
      raw: {
        transcript: pendingResults.transcript,
        noteMarkdown: pendingResults.noteMarkdown,
        noteHtml: pendingResults.noteHtml,
      },
      sections: pendingResults.sections,
      medicalCodes: pendingResults.medicalCodes,
    };
    onDictationCompleteRef.current(event, args.insertMode);
    setPendingResults(null);
  };

  // The chooser takes the panel over from the embed rather than opening on top of
  // it: the consultation is finished by this point, so keeping the iframe mounted
  // behind a dialog only held the microphone open and hid the form. Swapping it
  // out unmounts the embed, which releases both.
  //
  // Cancelling therefore ends the whole session rather than returning to the
  // embed — remounting it would start a second consultation, not resume the one
  // just finished.
  if (!skipChooser && pendingResults !== null) {
    const chooser = (
      <TortusResultChooser
        transcript={pendingResults.transcript}
        note={pendingResults.noteForDisplay}
        existingText={existingText}
        onPick={pickResult}
        onCancel={handleClose}
      />
    );

    return dockPanel ? (
      <TortusDockedPanel
        testId="tortusDictationChoice"
        stickyTop={stickyTop}
        bottomInset={bottomInset}
      >
        {chooser}
      </TortusDockedPanel>
    ) : (
      <PopupDialog
        open
        onClose={handleClose}
        maxWidth="sm"
        hasPadding={false}
        testId="tortusDictationChoice"
      >
        {chooser}
      </PopupDialog>
    );
  }

  const embedProps = {
    open,
    onClose: handleClose,
    tokenProvider,
    onClientReady: setClient,
    onValues: handleValues,
  };

  return dockPanel ? (
    <TortusAvtDrawer
      {...embedProps}
      stickyTop={stickyTop}
      bottomInset={bottomInset}
    />
  ) : (
    <TortusAvtModal {...embedProps} />
  );
};

const extractTranscript = (transcriptions: Transcription[]): string | null => {
  const text = transcriptions
    .filter((t: Transcription) => t.contentType === "text/plain")
    .map((t: Transcription) => t.content)
    .sort((a: string, b: string) => b.length - a.length)[0];
  return text && text.length > 0 ? text : null;
};

/**
 * Split a Tortus markdown medical note into `{ heading, level, body }`
 * sections. Walks the raw text for `^#{1,6} heading` lines and captures
 * the body until the next heading (or EOF).
 *
 * Caveats:
 *  - Headings are template-bound. Tortus controls the section names.
 *    Treat sections as data, not as named slots — do not switch app
 *    logic on heading text in shared infra.
 *  - A `#` inside a code block would be mis-parsed as a heading. Today's
 *    Tortus templates don't emit code blocks; if that changes, promote
 *    to a `unified`/`remark-parse` AST walker.
 */
const splitNoteIntoSections = (
  markdown: string | null,
): DictationNoteSection[] => {
  if (!markdown) return [];
  const headingRe = /^(#{1,6})\s+(.+?)\s*$/;
  const sections: DictationNoteSection[] = [];
  let current: { heading: string; level: number; bodyLines: string[] } | null =
    null;
  const flush = () => {
    if (!current) return;
    sections.push({
      heading: current.heading,
      level: current.level,
      body: current.bodyLines.join("\n").trim(),
    });
  };
  for (const line of markdown.split("\n")) {
    const m = headingRe.exec(line);
    const hashes = m?.[1];
    const heading = m?.[2];
    if (hashes !== undefined && heading !== undefined) {
      flush();
      current = {
        heading: heading.trim(),
        level: hashes.length,
        bodyLines: [],
      };
    } else if (current) {
      current.bodyLines.push(line);
    }
  }
  flush();
  return sections;
};

const stripHtml = (html: string): string => {
  if (typeof document === "undefined") return html;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

/**
 * Map our internal `ExtendedPatient` to Tortus's `PatientData` shape.
 * - `name` is built from given + family for display.
 * - `dateOfBirth` is only forwarded when it's a clean YYYY-MM-DD string,
 *   since Tortus rejects partial dates like "1985" or "1985-03".
 * - `identifier` carries the NHS number when present.
 */
const mapPatientForTortus = (
  patient: Patient | undefined,
):
  | {
      name?: string;
      givenName?: string;
      familyName?: string;
      dateOfBirth?: string;
      identifier?: { label: string; value: string };
    }
  | undefined => {
  if (!patient) return undefined;
  const fullName =
    `${patient.name.given ?? ""} ${patient.name.family ?? ""}`.trim();
  const isCleanDob =
    typeof patient.dateOfBirth === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(patient.dateOfBirth);
  // nhsNumber only lives on ExtendedPatient; check structurally so we
  // can take a bare Patient too (e.g. from SOAD pages).
  const nhsNumber =
    "nhsNumber" in patient && typeof patient.nhsNumber === "string"
      ? patient.nhsNumber
      : null;
  return {
    name: fullName.length > 0 ? fullName : undefined,
    givenName: patient.name.given || undefined,
    familyName: patient.name.family || undefined,
    dateOfBirth: isCleanDob ? (patient.dateOfBirth as string) : undefined,
    identifier: nhsNumber
      ? { label: "NHS Number", value: nhsNumber }
      : undefined,
  };
};
